
import React, { useState } from 'react';
import { Textarea } from '@/components/ui/textarea';
import { Button } from '@/components/ui/button';
import { Loader2, Send, Settings } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import ChatSettings, { ChatSettingsConfig } from './ChatSettings';

interface ChatInputProps {
  isLoading: boolean;
  onSendMessage: (text: string, settings: ChatSettingsConfig) => void;
  settings: ChatSettingsConfig;
  onSettingsChange: (settings: ChatSettingsConfig) => void;
}

const ChatInput: React.FC<ChatInputProps> = ({ 
  isLoading, 
  onSendMessage, 
  settings,
  onSettingsChange
}) => {
  const [inputValue, setInputValue] = useState('');

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = () => {
    if (!inputValue.trim() || isLoading) return;
    onSendMessage(inputValue, settings);
    setInputValue('');
  };

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <Textarea
          data-testid="chat-input"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Ask about your products, inventory, or sales..."
          className="flex-1 resize-none"
          disabled={isLoading}
          rows={2}
        />
        <div className="flex flex-col gap-2">
          <Popover>
            <PopoverTrigger asChild>
              <Button variant="outline" size="icon">
                <Settings className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-80">
              <ChatSettings 
                settings={settings} 
                onSettingsChange={onSettingsChange} 
              />
            </PopoverContent>
          </Popover>
          <Button 
            data-testid="send-message"
            onClick={handleSend}
            disabled={!inputValue.trim() || isLoading}
            size="icon"
          >
            {isLoading ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Send className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ChatInput;
